import { apiClient } from '@/lib/apiClient';
import type { Page } from '@/types/api';

import type {
  BulkResponse,
  Protocolo,
  ProtocoloCreate,
  ProtocoloListParams,
  ProtocoloUpdate,
} from './types';

const BASE = '/protocolos';

export const protocolosApi = {
  async create(data: ProtocoloCreate, signal?: AbortSignal): Promise<Protocolo> {
    const response = await apiClient.post<Protocolo>(BASE, data, { signal });
    return response.data;
  },

  /** Default: últimos 30 días si no hay filtros de fecha. */
  async list(
    params: ProtocoloListParams & { signal?: AbortSignal } = {}
  ): Promise<Page<Protocolo>> {
    const { signal, ...queryParams } = params;
    const response = await apiClient.get<Page<Protocolo>>(BASE, { params: queryParams, signal });
    return response.data;
  },

  async getById(uid: string, signal?: AbortSignal): Promise<Protocolo> {
    const response = await apiClient.get<Protocolo>(`${BASE}/${uid}`, { signal });
    return response.data;
  },

  async update(uid: string, data: ProtocoloUpdate, signal?: AbortSignal): Promise<Protocolo> {
    const response = await apiClient.patch<Protocolo>(`${BASE}/${uid}`, data, { signal });
    return response.data;
  },

  async remove(uid: string, signal?: AbortSignal): Promise<void> {
    await apiClient.delete(`${BASE}/${uid}`, { signal });
  },

  async actualizarInterpretacion(
    uid: string,
    data: { interpretacion_resultados: string | null },
    signal?: AbortSignal,
  ): Promise<Protocolo> {
    const response = await apiClient.patch<Protocolo>(
      `${BASE}/${uid}/interpretacion`,
      data,
      { signal },
    );
    return response.data;
  },

  async actualizarFacturado(
    uid: string,
    data: { facturado: boolean },
    signal?: AbortSignal,
  ): Promise<Protocolo> {
    const response = await apiClient.patch<Protocolo>(`${BASE}/${uid}/facturado`, data, { signal });
    return response.data;
  },

  async actualizarCobrado(
    uid: string,
    data: { cobrado: boolean },
    signal?: AbortSignal,
  ): Promise<Protocolo> {
    const response = await apiClient.patch<Protocolo>(`${BASE}/${uid}/cobrado`, data, { signal });
    return response.data;
  },

  async bulkActualizarFacturado(
    uids: string[],
    facturado: boolean,
    signal?: AbortSignal,
  ): Promise<BulkResponse> {
    const response = await apiClient.post<BulkResponse>(
      `${BASE}/bulk/facturado`,
      { uids, facturado },
      { signal },
    );
    return response.data;
  },

  async bulkActualizarCobrado(
    uids: string[],
    cobrado: boolean,
    signal?: AbortSignal,
  ): Promise<BulkResponse> {
    const response = await apiClient.post<BulkResponse>(
      `${BASE}/bulk/cobrado`,
      { uids, cobrado },
      { signal },
    );
    return response.data;
  },

  async bulkCerrar(uids: string[], signal?: AbortSignal): Promise<BulkResponse> {
    const response = await apiClient.post<BulkResponse>(`${BASE}/bulk/cerrar`, { uids }, { signal });
    return response.data;
  },
};
